import { ReactElement, useCallback, memo } from 'react';
import { Collapse } from '@mui/material';
import {
  KeyboardArrowRight as CollapsedIcon,
  KeyboardArrowDown as ExpandedIcon,
} from '@mui/icons-material';

import ComminSoonBadge from '../ComminSoonBadge';
import NewFeatureBagde from '../NewFeatureBagde';

import { NavigationListItemProps } from './types';
import NavigationListItemButtonAction from './ListItemButtonAction';
import NavigationSubListItem from './SubListItem';
import {
  NavigationListItemContainer,
  NavigationListItemIcon,
  NavigationListItemText,
  NavigationSubList,
} from './styles';

const NavigationListItem = ({
  id,
  label,
  icon: Icon,
  path,
  commingSoon,
  newActiveForVersion,
  validSubscriptionFeature,
  children,
  isActive = false,
  isExpanded = false,
  activeItem,
  onClick,
}: NavigationListItemProps): ReactElement => {
  const hasChildren = Boolean(children?.length);

  const handleClick = useCallback(() => {
    onClick?.({ id, path });
  }, [onClick, id, path]);

  const getIsChildActive = useCallback(
    (child: NonNullable<NavigationListItemProps['children']>[number]) =>
      activeItem === child.id || activeItem === child.path,
    [activeItem],
  );

  return (
    <NavigationListItemContainer hasChildren={hasChildren}>
      <NavigationListItemButtonAction
        validSubscriptionFeature={validSubscriptionFeature}
        onClick={handleClick}
        isActive={isActive}
        disabled={commingSoon}
      >
        <NavigationListItemIcon>{Icon}</NavigationListItemIcon>

        <NavigationListItemText
          primary={label}
          primaryTypographyProps={{ variant: 'inherit' }}
        />

        {commingSoon && <ComminSoonBadge />}

        {newActiveForVersion && (
          <NewFeatureBagde
            activeForVersion={newActiveForVersion}
            sx={{ marginRight: 0 }}
          />
        )}

        {hasChildren && (
          <NavigationListItemIcon sx={{ marginRight: 0 }}>
            {isExpanded ? <ExpandedIcon /> : <CollapsedIcon />}
          </NavigationListItemIcon>
        )}
      </NavigationListItemButtonAction>

      {hasChildren && (
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <NavigationSubList>
            {children?.map(
              ({ enabled = true, ...child }) =>
                enabled && (
                  <NavigationSubListItem
                    key={`navigation-sub-list-item-${child.id}`}
                    item={child}
                    isActive={getIsChildActive(child)}
                    onClick={onClick}
                  />
                ),
            )}
          </NavigationSubList>
        </Collapse>
      )}
    </NavigationListItemContainer>
  );
};

export default memo(NavigationListItem);
